import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Modal,
  ModalFooter,
  RadioButton,
  RadioButtonGroup,
  Icon,
  Spinner,
  Checkbox,
  TextArea,
  Selection,
} from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import useBarcodeSequenceGenerator from '../hooks/useBarcodeSequenceGenerator';

/**
 * Advanced Modal for configuring barcode generation settings
 * Simplified but actually functional version
 */
const BarcodeGenerationModal = ({
  open,
  onClose,
  onConfirm,
  itemCount = 1,
  enableAutoGenerator = false,
  autoGenerate = false,
  generateBarcodeValue,
  locationId,
  locationCode,
  existingBarcodes = [],
  title,
}) => {
  // Main generation settings
  const [generationMode, setGenerationMode] = useState('auto');
  const [startingBarcode, setStartingBarcode] = useState('');
  const [barcodeValidation, setBarcodeValidation] = useState({
    isValid: true,
    message: '',
    isChecking: false,
  });

  // Advanced features
  const [useImportedBarcodes, setUseImportedBarcodes] = useState(false);
  const [importedBarcodes, setImportedBarcodes] = useState('');
  const [useCustomFormat, setUseCustomFormat] = useState(false);
  const [customFormat, setCustomFormat] = useState('');
  const [previewCount, setPreviewCount] = useState(20);

  // File input ref
  const fileInputRef = useRef(null);

  // Generation modes
  const GENERATION_MODES = {
    AUTO: 'auto',
    SEQUENTIAL: 'sequential',
  };

  // Reset when modal opens/closes
  useEffect(() => {
    if (open) {
      setGenerationMode('auto');
      setStartingBarcode('');
      setUseImportedBarcodes(false);
      setImportedBarcodes('');
      setUseCustomFormat(false);
      setCustomFormat('');
      setBarcodeValidation({ isValid: true, message: '', isChecking: false });
    }
  }, [open]);

  // Validate barcode
  const validateBarcode = async (barcode) => {
    if (!barcode || barcode.trim() === '') {
      return { isValid: false, message: 'Barcode cannot be empty' };
    }
    if (existingBarcodes.includes(barcode)) {
      return { isValid: false, message: 'This barcode already exists' };
    }
    if (!/^[A-Za-z0-9-_]+$/.test(barcode)) {
      return { isValid: false, message: 'Invalid characters in barcode' };
    }
    if (barcode.length < 3) {
      return {
        isValid: false,
        message: 'Barcode too short (minimum 3 characters)',
      };
    }
    return { isValid: true, message: 'Barcode is valid' };
  };

  // Handle starting barcode change
  const handleStartingBarcodeChange = (e) => {
    const value = e.target.value;
    setStartingBarcode(value);

    clearTimeout(window.barcodeValidationTimeout);
    window.barcodeValidationTimeout = setTimeout(async () => {
      if (value) {
        setBarcodeValidation({ ...barcodeValidation, isChecking: true });
        const validation = await validateBarcode(value);
        setBarcodeValidation({
          isValid: validation.isValid,
          message: validation.message,
          isChecking: false,
        });
      } else {
        setBarcodeValidation({ isValid: true, message: '', isChecking: false });
      }
    }, 300);
  };

  // Generate sequential barcode
  const generateSequentialBarcode = (startBarcode, index) => {
    if (!startBarcode) return '';

    const match = startBarcode.match(/^(.*)(\d+)$/);
    if (match) {
      const prefix = match[1];
      const startNumber = parseInt(match[2], 10);
      const newNumber = startNumber + index;
      const originalLength = match[2].length;
      const paddedNumber = newNumber.toString().padStart(originalLength, '0');
      return `${prefix}${paddedNumber}`;
    } else {
      const paddedIndex = (index + 1).toString().padStart(3, '0');
      return `${startBarcode}${paddedIndex}`;
    }
  };

  // Apply custom format
  const applyCustomFormat = (format, index) => {
    const year = new Date().getFullYear();
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0');

    return format
      .replace(/{INDEX}/g, index)
      .replace(/{INDEX:(\d+)}/g, (match, digits) => index.toString().padStart(parseInt(digits), '0'))
      .replace(/{YEAR}/g, year)
      .replace(/{MONTH}/g, month)
      .replace(/{LOCATION}/g, locationCode || 'LOC');
  };

  // Generate final barcodes
  const generateFinalBarcodes = () => {
    if (useImportedBarcodes && importedBarcodes) {
      const imported = importedBarcodes
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .slice(0, itemCount);

      while (imported.length < itemCount) {
        imported.push('');
      }
      return imported;
    }

    if (
      generationMode === GENERATION_MODES.AUTO &&
      enableAutoGenerator &&
      autoGenerate
    ) {
      const barcodes = [];
      for (let i = 0; i < itemCount; i++) {
        barcodes.push(generateBarcodeValue(locationId, locationCode, i + 1));
      }
      return barcodes;
    }

    if (generationMode === GENERATION_MODES.SEQUENTIAL && startingBarcode) {
      const barcodes = [];
      for (let i = 0; i < itemCount; i++) {
        if (useCustomFormat && customFormat) {
          barcodes.push(applyCustomFormat(customFormat, i + 1));
        } else {
          barcodes.push(generateSequentialBarcode(startingBarcode, i));
        }
      }
      return barcodes;
    }

    return Array(itemCount).fill('');
  };

  // Get preview barcodes
  const getPreviewBarcodes = () => {
    const finalBarcodes = generateFinalBarcodes();
    return finalBarcodes.slice(0, Math.min(previewCount, itemCount));
  };

  // Handle file import
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setImportedBarcodes(text);
      setUseImportedBarcodes(true);
      setGenerationMode(GENERATION_MODES.SEQUENTIAL);
    };
    reader.readAsText(file);
  };

  // Export barcodes
  const handleExportBarcodes = () => {
    const barcodes = generateFinalBarcodes();
    const csvContent =
      'Index,Barcode\n' +
      barcodes
        .map((barcode, index) => `${index + 1},${barcode || 'EMPTY'}`)
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `barcodes_${new Date().toISOString().split('T')[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if configuration is valid
  const isConfigValid = () => {
    if (useImportedBarcodes) {
      return importedBarcodes && importedBarcodes.trim().length > 0;
    }

    if (generationMode === GENERATION_MODES.AUTO) {
      return enableAutoGenerator && autoGenerate;
    }

    if (generationMode === GENERATION_MODES.SEQUENTIAL) {
      return startingBarcode && barcodeValidation.isValid;
    }

    return false;
  };

  // Handle confirm
  const handleConfirm = () => {
    const finalBarcodes = generateFinalBarcodes();

    onConfirm({
      mode: useImportedBarcodes ? 'imported' : generationMode,
      startingBarcode:
        generationMode === GENERATION_MODES.SEQUENTIAL ? startingBarcode : null,
      customFormat: useCustomFormat ? customFormat : null,
      barcodes: finalBarcodes,
    });

    onClose();
  };

  const previewBarcodes = getPreviewBarcodes();
  const isAutoGenerationAvailable = enableAutoGenerator && autoGenerate;

  // Render footer for fixed footer
  const renderFooter = (
    <ModalFooter>
      <Button
        buttonStyle="primary"
        onClick={handleConfirm}
        disabled={!isConfigValid()}
        marginBottom0
      >
        <FormattedMessage
          id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.confirm"
          defaultMessage="Apply Settings"
        />
      </Button>
      <Button onClick={onClose} marginBottom0>
        <FormattedMessage
          id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.cancel"
          defaultMessage="Cancel"
        />
      </Button>
    </ModalFooter>
  );

  return (
    <Modal
      open={open}
      footer={renderFooter}
      label={
        title || (
          <FormattedMessage
            id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.label"
            defaultMessage="Advanced Barcode Generation Settings"
          />
        )
      }
      onClose={onClose}
      size="large"
    >
      <div style={{ padding: '20px' }}>
        {/* Item count info */}
        <div
          style={{
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            border: '1px solid #dee2e6',
          }}
        >
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.itemCount.label"
              defaultMessage="Items to create:"
            />
          </div>
          <div style={{ fontSize: '16px', color: '#495057' }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.itemCount.value"
              values={{ count: itemCount }}
              defaultMessage="{count} items"
            />
          </div>
        </div>

        {/* Import Section */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <Checkbox
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.useImported.label"
                  defaultMessage="Use imported barcode list (overrides other settings)"
                />
              }
              checked={useImportedBarcodes}
              onChange={(e) => {
                setUseImportedBarcodes(e.target.checked);
                if (e.target.checked) {
                  setGenerationMode(GENERATION_MODES.SEQUENTIAL);
                }
              }}
            />
          </div>

          {useImportedBarcodes && (
            <div
              style={{
                marginLeft: '20px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                <Button
                  buttonStyle="default"
                  onClick={() => fileInputRef.current?.click()}
                  marginBottom0
                >
                  <Icon icon="upload" />{' '}
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.importFile"
                    defaultMessage="Import from File"
                  />
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  style={{ display: 'none' }}
                  onChange={handleFileImport}
                />
              </div>

              <TextArea
                label={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.barcodeList.label"
                    defaultMessage="Barcode List (one per line)"
                  />
                }
                value={importedBarcodes}
                onChange={(e) => setImportedBarcodes(e.target.value)}
                rows={6}
                placeholder="BOOK2024001&#10;BOOK2024002&#10;BOOK2024003"
              />

              {importedBarcodes && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#6c757d',
                    marginTop: '4px',
                  }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.importedCount"
                    values={{
                      count: importedBarcodes
                        .split('\n')
                        .filter((line) => line.trim()).length,
                      needed: itemCount,
                    }}
                    defaultMessage="Imported: {count} barcodes (needed: {needed})"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Generation mode selection (only if not using imported) */}
        {!useImportedBarcodes && (
          <div style={{ marginBottom: '20px' }}>
            <RadioButtonGroup
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.generationMode.label"
                  defaultMessage="Generation Mode"
                />
              }
              name="generationMode"
              value={generationMode}
              onChange={(e) => setGenerationMode(e.target.value)}
            >
              <RadioButton
                value={GENERATION_MODES.AUTO}
                label={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.generationMode.auto.label"
                    defaultMessage="Automatic Generation"
                  />
                }
                disabled={!isAutoGenerationAvailable}
              />
              <RadioButton
                value={GENERATION_MODES.SEQUENTIAL}
                label={
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.generationMode.sequential.label"
                    defaultMessage="Sequential Generation"
                  />
                }
              />
            </RadioButtonGroup>

            {/* Auto generation info */}
            {generationMode === GENERATION_MODES.AUTO && (
              <div
                style={{
                  backgroundColor: isAutoGenerationAvailable
                    ? '#e8f5e8'
                    : '#fff3cd',
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '12px',
                  border: `1px solid ${isAutoGenerationAvailable ? '#c3e6c3' : '#ffeaa7'}`,
                }}
              >
                <div
                  style={{
                    fontSize: '14px',
                    color: isAutoGenerationAvailable ? '#2d5a2d' : '#856404',
                  }}
                >
                  {isAutoGenerationAvailable ? (
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.autoMode.enabled"
                      defaultMessage="✓ Automatic barcode generation is enabled"
                    />
                  ) : (
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.autoMode.disabled"
                      defaultMessage="⚠ Automatic generation is disabled - barcodes will be empty"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sequential generation settings */}
        {!useImportedBarcodes &&
          generationMode === GENERATION_MODES.SEQUENTIAL && (
            <div style={{ marginBottom: '20px' }}>
              {/* Starting barcode */}
              <div style={{ marginBottom: '16px' }}>
                <TextField
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.startingBarcode.label"
                      defaultMessage="Starting Barcode"
                    />
                  }
                  value={startingBarcode}
                  onChange={handleStartingBarcodeChange}
                  placeholder="BOOK2024001"
                  required
                  error={
                    !barcodeValidation.isValid ? barcodeValidation.message : ''
                  }
                  endControl={
                    barcodeValidation.isChecking ? (
                      <Spinner size="small" />
                    ) : barcodeValidation.isValid && startingBarcode ? (
                      <Icon icon="check-circle" status="success" />
                    ) : null
                  }
                />
              </div>

              {/* Custom format option */}
              <div style={{ marginBottom: '16px' }}>
                <Checkbox
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.useCustomFormat.label"
                      defaultMessage="Use custom format pattern"
                    />
                  }
                  checked={useCustomFormat}
                  onChange={(e) => setUseCustomFormat(e.target.checked)}
                />
              </div>

              {/* Custom format input */}
              {useCustomFormat && (
                <div style={{ marginLeft: '20px', marginBottom: '16px' }}>
                  <TextField
                    label={
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.formatPattern.label"
                        defaultMessage="Format Pattern"
                      />
                    }
                    value={customFormat}
                    onChange={(e) => setCustomFormat(e.target.value)}
                    placeholder="LIB{YEAR}{INDEX:4}"
                  />

                  <div
                    style={{
                      backgroundColor: '#e8f4fd',
                      padding: '8px',
                      borderRadius: '4px',
                      marginTop: '8px',
                      fontSize: '12px',
                    }}
                  >
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      Available placeholders:
                    </div>
                    <div>
                      <code>{'{INDEX}'}</code> - Sequential number (1, 2, 3...)
                    </div>
                    <div>
                      <code>{'{INDEX:4}'}</code> - Padded number (0001, 0002...)
                    </div>
                    <div>
                      <code>{'{YEAR}'}</code> - Current year (
                      {new Date().getFullYear()})
                    </div>
                    <div>
                      <code>{'{MONTH}'}</code> - Current month (
                      {(new Date().getMonth() + 1).toString().padStart(2, '0')})
                    </div>
                    <div>
                      <code>{'{LOCATION}'}</code> - Location code (
                      {locationCode || 'LOC'})
                    </div>
                  </div>
                </div>
              )}
            </div>
        )}

        {/* Preview Section */}
        <div style={{ marginBottom: '20px' }}>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '12px',
              alignItems: 'center',
            }}
          >
            <div style={{ fontWeight: '600' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.preview.title"
                defaultMessage="Preview:"
              />
            </div>

            <Selection
              value={previewCount}
              onChange={(value) => setPreviewCount(parseInt(value))}
              dataOptions={[
                { value: 10, label: '10' },
                { value: 20, label: '20' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
            />

            <Button
              buttonStyle="default"
              onClick={handleExportBarcodes}
              disabled={!isConfigValid()}
              marginBottom0
            >
              <Icon icon="download" />{' '}
              <FormattedMessage
                id="ui-plugin-inventory-templates.advancedBarcodeGenerationModal.export"
                defaultMessage="Export CSV"
              />
            </Button>
          </div>

          {previewBarcodes.length > 0 && (
            <div
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '12px',
              }}
            >
              <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {previewBarcodes.map((barcode, index) => (
                  <div key={index} style={{ padding: '2px 0' }}>
                    <span
                      style={{
                        color: '#6c757d',
                        minWidth: '40px',
                        display: 'inline-block',
                      }}
                    >
                      {index + 1}.
                    </span>
                    <span style={{ fontWeight: '500' }}>
                      {barcode || 'EMPTY'}
                    </span>
                  </div>
                ))}
                {itemCount > previewBarcodes.length && (
                  <div
                    style={{
                      fontStyle: 'italic',
                      color: '#6c757d',
                      marginTop: '8px',
                    }}
                  >
                    ... and {itemCount - previewBarcodes.length} more items
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </Modal>
  );
};

BarcodeGenerationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  itemCount: PropTypes.number,
  enableAutoGenerator: PropTypes.bool,
  autoGenerate: PropTypes.bool,
  generateBarcodeValue: PropTypes.func,
  locationId: PropTypes.string,
  locationCode: PropTypes.string,
  existingBarcodes: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.node,
};

export default BarcodeGenerationModal;
